// src/pages/UserPage/sections/RideShareSection.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Switch,
  Chip,
  Stack,
  Collapse,
  Alert,
  Grid,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Car,
  ArrowRight,
  Plus,
  Trash2,
  Pencil,
  CalendarDays,
  CalendarRange,
  Clock,
  Euro,
  MapPin,
  SlidersHorizontal,
} from 'lucide-react';
import { updateUserProfile } from '../../../firebase/user';
import { LocationAutocomplete } from '../../../shared/components/AutoComplete/LocationAutocomplete';
import { RouteDialog } from './rideShare/RouteDialog';
import {
  EMPTY_ROUTE,
  OCCURRENCE,
  OCCURRENCE_LABELS,
  PRICE_FALLBACK,
  DEEPLINK_KEYS,
  DEEPLINK_VALUES,
  routeOccurrence,
  formatOccurrence,
  formatPrice,
  toRoute,
  toDraft,
} from './rideShare/rideShareUtils';

// ─── RouteCard ────────────────────────────────────────────────────────────────

const RouteCard = ({ route, onEdit, onDelete, disabled }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const occ = routeOccurrence(route);
  const OccIcon = occ === OCCURRENCE.WEEKEND ? CalendarRange : CalendarDays;
  // Time only ever exists on a custom route that has a date
  const showTime = occ === OCCURRENCE.CUSTOM && route.date && route.time;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5,
      px: 1.5, py: 1.25,
      borderRadius: '12px',
      bgcolor: 'action.hover',
      border: '1px solid', borderColor: 'divider',
    }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: '8px', flexShrink: 0, mt: 0.25,
        bgcolor: alpha('#7c3aed', isDark ? 0.22 : 0.12),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <MapPin size={14} color="#7c3aed" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={700} noWrap>{route.origin}</Typography>
          <ArrowRight size={14} style={{ flexShrink: 0, opacity: 0.5 }} />
          <Typography variant="body2" fontWeight={700} noWrap>{route.destination}</Typography>
        </Box>

        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
          <Chip
            icon={<OccIcon size={12} />}
            label={formatOccurrence(route)}
            size="small" variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: '6px' } }}
          />
          {showTime && (
            <Chip
              icon={<Clock size={12} />}
              label={route.time}
              size="small" variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: '6px' } }}
            />
          )}
          <Chip
            icon={<Euro size={12} />}
            label={formatPrice(route.price)}
            size="small" variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: '6px' } }}
          />
        </Stack>
      </Box>

      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Tooltip title="Muokkaa" placement="top">
          <span>
            <IconButton
              size="small" onClick={() => onEdit(route)} disabled={disabled}
              sx={{ borderRadius: '8px' }}
            >
              <Pencil size={15} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Poista" placement="top">
          <span>
            <IconButton
              size="small" onClick={() => onDelete(route)} disabled={disabled}
              sx={{ borderRadius: '8px', color: 'text.secondary', '&:hover': { color: '#dc2626' } }}
            >
              <Trash2 size={15} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

// ─── RideShareSection ─────────────────────────────────────────────────────────

/**
 * Lets a courier opt their deliveries into ride sharing (kimppakyyti)
 * and declare regular routes they drive anyway.
 *
 * Stored on the user doc as:
 *   rideShare: {
 *     active: boolean,
 *     regularRoutes: [{
 *       id, origin, destination,
 *       occurrence: 'any' | 'weekend' | 'custom',
 *       date,   // only when occurrence === 'custom', else null
 *       time,   // only alongside a custom date, else null
 *       price,  // '' → "Omantunnon mukaan"
 *     }]
 *   }
 */
export const RideShareSection = ({ user, userData }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [searchParams] = useSearchParams();
  const sectionRef = useRef(null);
  const didScroll  = useRef(false);

  const [rideShare, setRideShare] = useState(() => ({
    active:        userData?.rideShare?.active ?? false,
    regularRoutes: userData?.rideShare?.regularRoutes ?? [],
  }));
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);
  const [highlight, setHighlight] = useState(false);

  // Inline quick-add
  const [quick, setQuick] = useState({ origin: '', destination: '' });

  // Dialog
  const [dialogOpen,    setDialogOpen]    = useState(false);
  const [dialogInitial, setDialogInitial] = useState(EMPTY_ROUTE);
  const [editingId,     setEditingId]     = useState(null);

  const routes = rideShare.regularRoutes || [];

  // ── keep local state in sync with the Firestore doc ───────────────────────
  useEffect(() => {
    setRideShare({
      active:        userData?.rideShare?.active ?? false,
      regularRoutes: userData?.rideShare?.regularRoutes ?? [],
    });
  }, [userData?.rideShare]);

  // ── deep link: /profiili?kimppakyyti → scroll here ────────────────────────
  const isDeepLinked = useMemo(() => {
    for (const k of DEEPLINK_KEYS) if (searchParams.has(k)) return true;
    for (const k of ['tab', 'section', 'osio', 'view']) {
      const v = searchParams.get(k);
      if (v && DEEPLINK_VALUES.includes(v.toLowerCase())) return true;
    }
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '').toLowerCase();
      if (DEEPLINK_VALUES.includes(hash)) return true;
    }
    return false;
  }, [searchParams]);

  useEffect(() => {
    if (!isDeepLinked || didScroll.current || !sectionRef.current) return;
    didScroll.current = true;
    // wait a frame so the tab panel + fixed header have settled
    const t = setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlight(true);
      setTimeout(() => setHighlight(false), 2400);
    }, 350);
    return () => clearTimeout(t);
  }, [isDeepLinked]);

  // ── persistence ───────────────────────────────────────────────────────────
  // NOTE: updateDoc replaces the whole `rideShare` map, so we always write it whole.
  const persist = useCallback(async (next) => {
    if (!user?.uid) return false;
    const prev = rideShare;
    setRideShare(next);          // optimistic
    setSaving(true);
    setError(null);
    try {
      await updateUserProfile(user.uid, { rideShare: next });
      return true;
    } catch (err) {
      console.error('rideShare save failed', err);
      setRideShare(prev);        // revert
      setError('Tallennus epäonnistui. Yritä uudelleen.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [user?.uid, rideShare]);

  const handleToggleActive = (_, checked) => {
    persist({ ...rideShare, active: checked, regularRoutes: routes });
  };

  const commitRoute = async (draft, id) => {
    if (!draft.origin.trim() || !draft.destination.trim()) {
      setError('Lähtöpaikka ja määränpää vaaditaan.');
      return false;
    }
    const route = toRoute(draft, id);
    const nextRoutes = id
      ? routes.map((r) => (r.id === id ? route : r))
      : [...routes, route];

    // adding a route implies you want the feature on
    return persist({ active: true, regularRoutes: nextRoutes });
  };

  // ── inline "Luo" ──────────────────────────────────────────────────────────
  const canQuickCreate = quick.origin.trim() && quick.destination.trim() && !saving;

  const handleQuickCreate = async () => {
    // EMPTY_ROUTE carries occurrence: 'any', so defaults apply automatically
    const ok = await commitRoute({ ...EMPTY_ROUTE, ...quick }, null);
    if (ok) setQuick({ origin: '', destination: '' });
  };

  // ── "Tarkenna" — carries whatever is already typed inline ─────────────────
  const handleRefine = () => {
    setDialogInitial({ ...EMPTY_ROUTE, ...quick });
    setEditingId(null);
    setError(null);
    setDialogOpen(true);
  };

  // ── edit existing ─────────────────────────────────────────────────────────
  const handleEdit = (route) => {
    setDialogInitial(toDraft(route));
    setEditingId(route.id);
    setError(null);
    setDialogOpen(true);
  };

  const handleDialogSave = async (draft) => {
    const ok = await commitRoute(draft, editingId);
    if (ok) {
      setDialogOpen(false);
      if (!editingId) setQuick({ origin: '', destination: '' }); // consumed by the dialog
      setEditingId(null);
    }
  };

  const handleDialogClose = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingId(null);
    setError(null);
  };

  const handleDeleteRoute = (route) => {
    persist({ ...rideShare, regularRoutes: routes.filter((r) => r.id !== route.id) });
  };

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <Box
      ref={sectionRef}
      id="kimppakyyti"
      sx={{
        p: { xs: 2, sm: 3 },
        borderTop: '1px solid',
        borderColor: 'divider',
        scrollMarginTop: { xs: '96px', sm: '112px' },
        transition: 'background-color 0.4s ease',
        bgcolor: highlight ? alpha('#7c3aed', isDark ? 0.16 : 0.07) : 'transparent',
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            bgcolor: isDark ? '#2e1065' : '#f5f3ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid', borderColor: alpha('#7c3aed', 0.2),
          }}>
            <Car size={18} color="#7c3aed" strokeWidth={1.75} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>Kimppakyyti</Typography>
              {rideShare.active && (
                <Chip
                  label="Käytössä"
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.68rem', fontWeight: 700,
                    bgcolor: isDark ? '#052e16' : '#f0fdf4',
                    color: '#16a34a',
                    border: '1px solid', borderColor: alpha('#16a34a', 0.25),
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Ota kyytiläisiä mukaan kuljetuksiisi
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {saving && <CircularProgress size={16} />}
          <Switch
            checked={!!rideShare.active}
            onChange={handleToggleActive}
            disabled={saving}
            size="small"
          />
        </Box>
      </Box>

      {/* ── Error (section-level; the dialog renders its own) ── */}
      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Explainer (inactive state) ── */}
      {!rideShare.active && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.65 }}>
          Kun kimppakyyti on käytössä, ottamasi kuljetukset näkyvät myös kyytiä etsiville.
          Voit lisäksi ilmoittaa säännölliset reitit, joita ajat muutenkin — näin saat
          matkakumppaneita ja lisätuloa samalla vaivalla.
        </Typography>
      )}

      {/* ── Active state ── */}
      <Collapse in={!!rideShare.active}>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Kuljetuksesi ovat nyt avoimia myös kyytiläisille. Lisää halutessasi säännölliset
            reittisi — pelkkä lähtöpaikka ja määränpää riittää.
          </Typography>

          {/* Routes */}
          {routes.length > 0 && (
            <Stack spacing={1}>
              {routes.map((r) => (
                <RouteCard
                  key={r.id}
                  route={r}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRoute}
                  disabled={saving}
                />
              ))}
            </Stack>
          )}

          {/* ── Inline quick-add: Mistä → Minne → Luo / Tarkenna ── */}
          <Box sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: '12px',
            border: '1px dashed', borderColor: 'divider',
          }}>
            <Grid container spacing={1.5} alignItems="flex-start">
              <Grid item xs={12} sm={4}>
                <LocationAutocomplete
                  label="Mistä"
                  placeholder="esim. Helsinki"
                  value={quick.origin}
                  onChange={(val) => { setQuick((p) => ({ ...p, origin: val })); setError(null); }}
                  onLocationSelect={(loc) =>
                    setQuick((p) => ({ ...p, origin: loc.city || loc.address || p.origin }))}
                  showDetectLocation
                  countryCode="fi"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LocationAutocomplete
                  label="Minne"
                  placeholder="esim. Joensuu"
                  value={quick.destination}
                  onChange={(val) => { setQuick((p) => ({ ...p, destination: val })); setError(null); }}
                  onLocationSelect={(loc) =>
                    setQuick((p) => ({ ...p, destination: loc.city || loc.address || p.destination }))}
                  countryCode="fi"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleQuickCreate}
                    disabled={!canQuickCreate}
                    startIcon={saving && !dialogOpen
                      ? <CircularProgress size={15} color="inherit" />
                      : <Plus size={15} />}
                    sx={{
                      flex: 1, borderRadius: '10px', textTransform: 'none',
                      fontWeight: 700, boxShadow: 'none', whiteSpace: 'nowrap',
                      '&:hover': { boxShadow: 'none' },
                    }}
                  >
                    Luo
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRefine}
                    disabled={saving}
                    startIcon={<SlidersHorizontal size={15} />}
                    sx={{
                      flex: 1, borderRadius: '10px', textTransform: 'none',
                      fontWeight: 600, whiteSpace: 'nowrap',
                    }}
                  >
                    Tarkenna
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Ilman tarkennusta: {OCCURRENCE_LABELS[OCCURRENCE.ANY].toLowerCase()},{' '}
                  {PRICE_FALLBACK.toLowerCase()}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Collapse>

      {/* ── Dialog: Tarkenna / Muokkaa ── */}
      <RouteDialog
        open={dialogOpen}
        initial={dialogInitial}
        isEdit={!!editingId}
        saving={saving}
        error={dialogOpen ? error : null}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        onDismissError={() => setError(null)}
      />
    </Box>
  );
};

export default RideShareSection;